import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Updater } from "use-immer";

import { ColorPrimary } from "@intelligems/ig-theme";
import {
  Announcement,
  BookOutlined,
  Campaign,
  ChevronLeft,
  ChevronRight,
  EmojiPeopleOutlined,
  ExitToApp,
  HelpOutlineOutlined,
  MailOutline,
} from "@mui/icons-material";
import {
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useIsAdmin } from "../../api/user-auths/is-admin";
import { useLogout } from "../../api/user-auths/logout";
import { setSideNavCollapsed } from "../../store/slices/appSettings";
import { setDialog } from "../../store/slices/global";
import { contactSupport } from "../../utils/contactSupport";
import { knowledgeBase } from "../../utils/linkSheet";
import openNewTab from "../../utils/openNewTab";
import { BottomRoutes } from "../Utils/routes";

import { AnnouncementButton } from "./AnnouncementButton";
import ListItemLink from "./ListItemLink";

const PREFIX = "SideNavBarFooter";

const classes = {
  selected: `${PREFIX}-selected`,
  root: `${PREFIX}-root`,
  selectedCollapse: `${PREFIX}-selectedCollapse`,
  rootCollapse: `${PREFIX}-rootCollapse`,
  drawerFooter: `${PREFIX}-drawerFooter`,
  buttonContainer: `${PREFIX}-buttonContainer`,
  textMargin: `${PREFIX}-textMargin`,
};

const StyledList = styled(List)(({ theme }) => ({
  [`& .${classes.selected}`]: {
    "&.MuiListItemButton-root": {
      "&.Mui-selected": {
        color: ColorPrimary.light3,
        background: "transparent",
        "&:hover, &:focus": {
          background: "transparent",
          color: ColorPrimary.light3,
        },
        "&:after": {
          width: 5,
          height: 50,
          background: "rgb(25, 65, 225)",
          transform: "translateX(0)",
        },
      },
    },
  },

  [`& .${classes.root}`]: {
    "&.MuiListItemButton-root": {
      color: "white",
      "&:hover, &:focus": {
        background: "transparent",
        color: ColorPrimary.light3,
      },
    },
  },

  [`& .${classes.selectedCollapse}`]: {
    "&.MuiListItemButton-root": {
      "&.Mui-selected": {
        color: "white",
        background: "transparent",
        "&:hover": {
          background: "transparent",
          color: ColorPrimary.light3,
        },
      },
    },
  },

  [`& .${classes.rootCollapse}`]: {
    "&.MuiListItemIconButton-root": {
      color: "white",
      margin: 0,
      font: "1em",
      fontFamily: theme.typography.fontFamily,
      "&:hover": {
        background: "transparent",
        color: ColorPrimary.light3,
      },
    },
  },

  [`& .${classes.buttonContainer}`]: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },

  [`& .${classes.textMargin}`]: {
    marginLeft: 15,
  },
}));

interface SideNavBarFooterProps {
  drawerOpen: boolean;
}

export const SideNavBarFooter = ({ drawerOpen }: SideNavBarFooterProps) => {
  const isAdmin = useIsAdmin();
  const logout = useLogout();

  const handleLogout = () => {
    logout.mutate();
  };
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const helpOpen = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <StyledList style={{ padding: 0 }} aria-label="bottom navigation">
      <ListItemLink
        hasAccess={true}
        drawerOpen={drawerOpen}
        Route={BottomRoutes().integrations}
      />
      {isAdmin && (
        <ListItemLink
          hasAccess={true}
          exact={true}
          drawerOpen={drawerOpen}
          Route={BottomRoutes().performance}
        />
      )}
      {isAdmin && (
        <ListItemLink
          hasAccess={true}
          drawerOpen={drawerOpen}
          Route={BottomRoutes().configCompare}
        />
      )}
      <ListItemLink
        hasAccess={true}
        drawerOpen={drawerOpen}
        Route={BottomRoutes().settings}
        exact={true}
      />
      <AnnouncementButton />
      <ListItemButton
        dense={true}
        aria-label="help"
        classes={{ selected: classes.selected, root: classes.root }}
        onClick={(event) => {
          handleClick(event);
        }}
      >
        <div className={classes.buttonContainer}>
          <HelpOutlineOutlined />
          <ListItemText
            disableTypography={true}
            sx={{
              marginLeft: "15px",
              fontSize: "14px",
            }}
            primary={"Help"}
          />
        </div>
      </ListItemButton>
      <Menu
        style={{ justifyContent: "flex-start" }}
        anchorOrigin={{ horizontal: "right", vertical: "center" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorEl={anchorEl}
        open={helpOpen}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            openNewTab(knowledgeBase, "Intelligems Knowledge Base");
            handleClose();
          }}
        >
          <BookOutlined style={{ marginRight: 5, color: "#757575" }} />
          Knowledge Base
        </MenuItem>
        <MenuItem
          onClick={() => {
            contactSupport();
            handleClose();
          }}
        >
          <MailOutline style={{ marginRight: 5, color: "#757575" }} />
          Contact support
        </MenuItem>
        <MenuItem
          onClick={() => {
            dispatch(setDialog({ dialog: "onboarding", open: true }));
            handleClose();
          }}
        >
          <EmojiPeopleOutlined style={{ marginRight: 5, color: "#757575" }} />
          Open Welcome Message
        </MenuItem>
      </Menu>
      <ListItemButton
        aria-label="log out"
        classes={{ selected: classes.selected, root: classes.root }}
        onClick={() => {
          handleLogout();
        }}
      >
        <ExitToApp />
        <ListItemText
          disableTypography={true}
          sx={{
            marginLeft: "15px",
            fontSize: "14px",
          }}
          primary={"Log Out"}
        />
      </ListItemButton>
      <ListItemButton
        aria-label="collapse"
        classes={{
          selected: classes.selectedCollapse,
          root: classes.rootCollapse,
        }}
        selected
        onClick={() => {
          dispatch(setSideNavCollapsed());
          // setDrawerOpen((prev) => !prev);
        }}
      >
        <div className={classes.buttonContainer}>
          {drawerOpen ? <ChevronLeft /> : <ChevronRight />}
          <ListItemText
            disableTypography={true}
            sx={{
              marginLeft: "15px",
              fontSize: "14px",
            }}
            primary={"Collapse"}
          />
        </div>
      </ListItemButton>
    </StyledList>
  );
};
